import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemedBackground } from '../components/ThemedBackground'
import { useTheme } from '../lib/theme-provider'
import { getUserProfile } from '../utils/storage'

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

interface UserInfo {
  id: string
  nickname: string
}

export function Login() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const [nickname, setNickname] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 加载已保存的用户配置
  useEffect(() => {
    const profile = getUserProfile()
    if (profile?.nickname) {
      setNickname(profile.nickname)
    }
  }, [])

  function saveUser(user: UserInfo) {
    localStorage.setItem('avalon_user', JSON.stringify(user))
  }

  function getUser(): UserInfo | null {
    const stored = localStorage.getItem('avalon_user')
    if (!stored) return null
    try {
      return JSON.parse(stored)
    } catch {
      return null
    }
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    
    if (!nickname.trim()) {
      setError('请输入昵称')
      return
    }

    if (nickname.trim().length < 2) {
      setError('昵称至少需要 2 个字符')
      return
    }

    if (nickname.trim().length > 20) {
      setError('昵称不能超过 20 个字符')
      return
    }

    setLoading(true)
    setError('')

    try {
      let user = getUser()
      
      if (!user) {
        user = {
          id: generateUUID(),
          nickname: nickname.trim()
        }
        saveUser(user)
      } else {
        user.nickname = nickname.trim()
        saveUser(user)
      }

      navigate('/online-lobby')
    } catch (err: any) {
      console.error('Login error:', err)
      setError(err.message || '登录失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ThemedBackground pageName="home">
      <div className="min-h-screen flex items-center justify-center px-4">
        <div 
          className="w-full max-w-md p-8 rounded-3xl shadow-2xl"
          style={{ 
            backgroundColor: theme.colors['bg-card'],
            border: `1px solid ${theme.colors['border-primary']}`
          }}
        >
          {/* Logo / Title */}
          <div className="text-center mb-8">
            <h1 
              className="text-4xl font-bold mb-2"
              style={{ color: theme.colors['accent-gold'] }}
            >
              阿瓦隆
            </h1>
            <p style={{ color: theme.colors['text-secondary'] }}>
              多人身份推理游戏
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label 
                htmlFor="nickname" 
                className="block text-sm font-medium mb-2"
                style={{ color: theme.colors['text-primary'] }}
              >
                请输入昵称
              </label>
              <input
                id="nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="给自己起个好听的名字..."
                disabled={loading}
                className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all"
                style={{
                  backgroundColor: theme.colors['bg-secondary'],
                  borderColor: theme.colors['border-primary'],
                  color: theme.colors['text-primary'],
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.colors['accent-gold']
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.colors['border-primary']
                }}
              />
              {error && (
                <p className="mt-2 text-sm" style={{ color: theme.colors['accent-red'] }}>
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{
                backgroundColor: loading ? theme.colors['text-muted'] : theme.colors['accent-gold'],
                color: theme.colors['bg-primary']
              }}
            >
              {loading ? '登录中...' : '开始游戏'}
            </button>
          </form>

          {/* Tips */}
          <div className="mt-8 pt-6 border-t" style={{ borderColor: theme.colors['border-primary'] }}>
            <p className="text-sm text-center" style={{ color: theme.colors['text-secondary'] }}>
              💡 输入昵称后即可创建或加入房间
            </p>
            <p className="text-sm text-center mt-2" style={{ color: theme.colors['text-secondary'] }}>
              🎮 支持 5-13 人在线对战
            </p>
          </div>
        </div>
      </div>
    </ThemedBackground>
  )
}
